/**
 * #4447 GUSSTATION: PIN sign-in on registered QC stations.
 *
 * The one place the keypad, the Settings PIN card, the Stations admin tab and
 * the server agree on the shape of a station, a PIN and a PIN session. The
 * server's enforcement lives in src/server/middleware/pin-session.ts and
 * src/server/routes/station*.ts; this file holds only constants, response
 * types and the pure path policy both sides read.
 */

/** The station home pages: keys of STATIONS in src/client/lib/station-home.ts. */
export const STATION_HOME_PAGES = ["compressions", "shrinkages"] as const;
export type StationHomePage = (typeof STATION_HOME_PAGES)[number];

export function isStationHomePage(value: unknown): value is StationHomePage {
  return typeof value === "string" && (STATION_HOME_PAGES as readonly string[]).includes(value);
}

/** The admin setting: new PINs have 6 digits unless an admin switches to 4. */
export const PIN_LENGTHS = [4, 6] as const;
export type PinLength = (typeof PIN_LENGTHS)[number];
export const DEFAULT_PIN_LENGTH: PinLength = 6;

export function isPinLength(value: unknown): value is PinLength {
  return value === 4 || value === 6;
}

/** Every PIN session ends 12 hours after it started, whatever else happens. */
export const PIN_SESSION_MAX_MS = 12 * 60 * 60 * 1000;
/** Server backstop: a PIN session nothing has touched for this long is refused and ended. */
export const PIN_SESSION_IDLE_MS = 15 * 60 * 1000;
/** Client: no touch for this long returns the screen to the keypad. */
export const PIN_SESSION_CLIENT_IDLE_MS = 2 * 60 * 1000;
/** This many wrong PINs at one station inside the rolling window locks its keypad. */
export const PIN_LOCK_THRESHOLD = 10;
export const PIN_LOCK_WINDOW_MS = 24 * 60 * 60 * 1000;

export const PIN_SESSION_FULL_SIGNIN_CODE = "PIN_SESSION_FULL_SIGNIN_REQUIRED";
export const PIN_SESSION_FULL_SIGNIN_MESSAGE =
  "Sign in with Microsoft to do this. A PIN sign-in can enter QC results but cannot change people, roles or stations.";
export const PIN_SESSION_ENDED_CODE = "PIN_SESSION_ENDED";

/** What the server knows about the PIN session behind a request; `/api/rbac/me` carries it as `pin_session`. */
export interface PinSessionInfo {
  station_id: string;
  station_name: string;
  home_page: StationHomePage;
  started_at: string;
}

/**
 * Paths a PIN session may never reach, whatever the person's role (Hugo,
 * CAIRBAC: ceo_ops is not an admin role and still holds User Management
 * edit, so the refusal is by path, not role). Writes under /api/rbac, all of
 * /api/admin and the admin-only prefixes beside it, station registration and
 * settings, PIN issue or reset, and the lock clear. GET on /api/rbac stays
 * open: /api/rbac/me is how the app learns its own permissions.
 *
 * PR 4580 review: the admin-only writes that live outside those prefixes are
 * judged one by one. Refused: anything that changes people, grants, settings
 * or other admin configuration, sends mail, or runs SQL against the ERP.
 * Kept: QC data work (an admin removing an abandoned test-monitor entry),
 * schedule events, what's-new acks, feature requests and feedback, all of
 * which the role check still judges.
 */
export function isPinSessionRefusedPath(method: string, path: string): boolean {
  const write = method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
  const under = (prefix: string) => path === prefix || path.startsWith(`${prefix}/`);
  if (under("/api/admin")) return true;
  if (under("/api/station-admin")) return true;
  // #4468: staff questions and the help inbox pilot list, reads included.
  if (under("/api/help-inbox")) return true;
  if (path === "/api/station/unlock") return true;
  if (path === "/api/station/pin" && write) return true;
  if (path === "/api/station/pin/issue") return true;
  // Admin SQL and raw table reads against the Ostendo ERP (GH #52): not QC data work.
  if (path === "/api/ostendo/sql" || under("/api/ostendo/table")) return true;
  if (!write) return false;
  if (under("/api/rbac")) return true;
  if (under("/api/access-requests")) return true;
  // #4497: team facts reach every user's Analyst prompt; admin configuration.
  if (under("/api/team-facts")) return true;
  if (under("/api/email-subscriptions")) return true;
  if (under("/api/digest-recipients")) return true;
  if (under("/api/ostendo-live")) return true;
  if (under("/api/portal/admin")) return true;
  // Grant-shaped: who may connect Outlook.
  if (under("/api/outlook/allowed-users")) return true;
  // Sends the approval-cycle report to any recipients named in the query.
  if (path === "/api/reports/approval-cycle/send") return true;
  // Case desk owners: admin configuration.
  if (under("/api/cases/desks")) return true;
  // Cost mapping: admin configuration.
  if (path === "/api/costs/mapping") return true;
  // ScheduleIt admin operations and the sync-cadence setting; /events stays with the role check.
  if (["/api/schedule/import", "/api/schedule/sync", "/api/schedule/backfill", "/api/schedule/parity", "/api/schedule/sync-cadence"].includes(path)) return true;
  // Changelog authoring and request triage: admin content; /changelog-ingest is keyed, not a session route.
  // Approve files a GitHub issue as the App (#926), so a PIN session never reaches it or reject.
  if (under("/api/updates/changelog")) return true;
  if (/^\/api\/updates\/requests\/[^/]+\/(status|approve|reject)$/.test(path)) return true;
  return false;
}

/**
 * better-auth's own routes (/api/auth/*) answer before pinSessionMiddleware
 * runs, so a PIN session is judged there by this list instead. It keeps
 * reading itself, ending itself and the OAuth flows that mint a NEW session;
 * update-user, list or revoke sessions, link, unlink and delete act on the
 * person and need a Microsoft sign-in.
 */
export function isPinSessionAllowedAuthRoute(method: string, path: string): boolean {
  if (method === "HEAD" || method === "OPTIONS") return true;
  if (method === "GET" && path === "/api/auth/get-session") return true;
  if (method === "POST" && path === "/api/auth/sign-out") return true;
  if (path.startsWith("/api/auth/sign-in/")) return true;
  if (path.startsWith("/api/auth/callback/")) return true;
  if (path === "/api/auth/ok" || path === "/api/auth/error") return true;
  return false;
}

/* ---- API shapes ---- */

export interface StationSummary {
  id: string;
  name: string;
  home_page: StationHomePage;
}

/** GET /api/station/me: what this browser is, and who is at it. */
export interface StationMeResponse {
  registered: boolean;
  station: StationSummary | null;
  revoked: boolean;
  locked: boolean;
  pin_length: PinLength;
  /** The session behind the request's cookies, if any. */
  session: {
    user: { id: string; name: string | null };
    /** True for a PIN session; false for a Microsoft (or Google) one. */
    pin: boolean;
    /** A full sign-in that may clear this station's lock (admin or QC manager). */
    can_unlock: boolean;
  } | null;
}

/** POST /api/station/pin/signin, 200. */
export interface StationSignInResponse {
  ok: true;
  home_page: StationHomePage;
  user: { id: string; name: string | null };
}

/** POST /api/station/pin/signin, refused. */
export interface StationSignInRefusal {
  error: string;
  code: "no_station" | "station_revoked" | "station_locked" | "wrong_pin" | "bad_pin" | "rate_limited";
  /** On wrong_pin: wrong PINs left before the station locks. */
  remaining?: number;
}

/** GET /api/station/pin: the signed-in person's own PIN status (never the digits). */
export interface StationPinStatus {
  eligible: boolean;
  has_pin: boolean;
  issued_at: string | null;
  pin_length: PinLength;
  pin_session: boolean;
}

export interface StationPinIssued {
  pin: string;
  pin_length: PinLength;
  issued_at: string;
}

export interface StationRowView extends StationSummary {
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  revoked_at: string | null;
  locked_at: string | null;
  open_sessions: number;
  wrong_pins_24h: number;
}

export interface StationListResponse {
  stations: StationRowView[];
  pin_length: PinLength;
  pins_issued: number;
}

export interface StationSessionLogRow {
  session_id: string;
  station_id: string;
  station_name: string;
  user_id: string;
  user_name: string | null;
  started_at: string;
  last_seen_at: string;
  ended_at: string | null;
  end_reason: string | null;
}

export interface StationAttemptLogRow {
  station_id: string;
  station_name: string;
  attempted_at: string;
  outcome: string;
  user_name: string | null;
  /** Presses this row stands for: 1 for a wrong PIN; a 'locked' row carries the whole lock period's count. */
  count: number;
}

export interface StationLogResponse {
  sessions: StationSessionLogRow[];
  failed_attempts: StationAttemptLogRow[];
}
